/**
 * Aviso da Fase 1 da migração de domínio (multitenancy por subdomínio).
 * Aparece para quem acessa pelo formato antigo (webip.app/<tenant>/...),
 * mostra o novo endereço e redireciona automaticamente após a contagem.
 * Desabilitado quando VITE_TENANT_BASE_DOMAIN não está configurado.
 */
import { useEffect, useRef, useState } from 'react';
import AlertBox from '@/components/layout/AlertBox';
import Botao from '@/components/Botao';
import { buildSubdomainUrl, resolveTenantFromWindow, TENANT_BASE_DOMAIN } from '@/lib/tenant';
import styles from './LegacyDomainBanner.module.css';

const DISMISS_KEY = 'legacy-domain-notice-dismissed-at';
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 dias
const COUNTDOWN_SECONDS = 15;

function isDismissed(): boolean {
    try {
        const dismissedAt = Number(localStorage.getItem(DISMISS_KEY));
        return Number.isFinite(dismissedAt) && dismissedAt > 0 && Date.now() - dismissedAt < DISMISS_TTL_MS;
    } catch {
        return false;
    }
}

export const LegacyDomainBanner = () => {
    const [visible, setVisible] = useState(false);
    const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
    const [redirecting, setRedirecting] = useState(false);
    const [copied, setCopied] = useState(false);
    const [newUrl, setNewUrl] = useState<string | null>(null);
    const wrapRef = useRef<HTMLDivElement | null>(null);

    // Empurra o layout para baixo em vez de cobrir o menu: publica a altura do
    // banner em --legacy-banner-h, consumida por LayoutUsuario.module.css.
    useEffect(() => {
        if (!visible) return;

        const el = wrapRef.current;
        if (!el) return;

        const apply = () => {
            document.documentElement.style.setProperty('--legacy-banner-h', `${el.offsetHeight}px`);
        };

        apply();
        window.addEventListener('resize', apply);
        return () => {
            document.documentElement.style.setProperty('--legacy-banner-h', '0px');
            window.removeEventListener('resize', apply);
        };
    }, [visible]);

    useEffect(() => {
        if (!TENANT_BASE_DOMAIN) return;

        const { tenant, source } = resolveTenantFromWindow();
        // Página pública do portal não participa da migração. Link de acesso único
        // também não: é consumido (e a navegação sai daqui) em menos de 1s — o
        // redirect de 15s deste banner fica armado com a URL do token já gasto e
        // dispara depois, sem saber que o usuário já saiu desta página.
        if (
            source !== 'path' ||
            !tenant ||
            window.location.pathname.startsWith('/portal') ||
            window.location.pathname.endsWith('/login/token')
        ) return;

        if (isDismissed()) return;

        setNewUrl(buildSubdomainUrl(tenant, window.location.pathname + window.location.search));
        setVisible(true);
    }, []);

    useEffect(() => {
        if (!visible || redirecting) return;

        const interval = setInterval(() => {
            setCountdown(prev => prev - 1);
        }, 1000);

        return () => clearInterval(interval);
    }, [visible, redirecting]);

    useEffect(() => {
        if (visible && !redirecting && countdown <= 0 && newUrl) {
            window.location.replace(newUrl);
        }
    }, [visible, redirecting, countdown, newUrl]);

    if (!visible || !newUrl) return null;

    const handleStay = () => setRedirecting(true);

    const handleNever = () => {
        try {
            localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch {
            // localStorage indisponível — apenas fecha o aviso nesta sessão.
        }
        setVisible(false);
    };

    const handleCopy = () => {
        navigator.clipboard?.writeText(newUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(() => { });
    };

    return (
        <div className={styles.wrap} ref={wrapRef}>
            <AlertBox variant="warning" style={{ margin: 0 }}>
                <div className={styles.content}>
                    <div className={styles.text}>
                        <strong>Este endereço está mudando.</strong> Use o novo domínio:{' '}
                        <span className={styles.newUrl}>{newUrl}</span>
                        {redirecting
                            ? ' — atualize seus favoritos.'
                            : ` — redirecionando em ${countdown}s.`}
                    </div>
                    <div className={styles.actions}>
                        <Botao variant="primary" onClick={() => window.location.replace(newUrl)}>
                            Ir agora
                        </Botao>
                        <Botao variant="outline-secondary" onClick={handleCopy}>
                            {copied ? 'Copiado!' : 'Copiar'}
                        </Botao>
                        {!redirecting && (
                            <Botao variant="none-secondary" onClick={handleStay}>
                                Permanecer aqui
                            </Botao>
                        )}
                        <Botao variant="none-secondary" onClick={handleNever}>
                            Não avisar novamente
                        </Botao>
                    </div>
                </div>
            </AlertBox>
        </div>
    );
};

export default LegacyDomainBanner;